/**
 * Разбирает срок гарантии из инвентаря на составляющие.
 *
 * ## Зачем
 *
 * Поле приходит одной строкой и по-французски: «7 ans / 150 000 km». На
 * витрине это выглядело как непонятный шифр в самом заметном месте карточки
 * фактов, а инфографике там показывать было нечего: доля «9 из 9» — это
 * 100%, и сплошная полоса во всю ширину не сообщает ничего (#1330).
 *
 * Разобранная строка даёт два настоящих факта: сколько лет и до какого
 * пробега. Их и показываем — вместо шкалы, которой здесь нечего делить.
 *
 * ## Правило
 *
 * Ничего не додумываем. Не разобралось — возвращаем `raw`, и интерфейс
 * печатает исходную строку как есть. Придумать «7 лет» из строки, которую мы
 * не поняли, значит соврать про гарантию — то есть про обязательство дилера.
 *
 * @param {string} value — значение поля `warrantyTerm`.
 * @returns {{ raw: string, years: number|null, km: number|null }}
 */
export function parseWarrantyTerm(value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return { raw: '', years: null, km: null };

  const key = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  // «ans» / «an» / «years» / «jahre» / «лет». Число обязано стоять ПЕРЕД
  // единицей: «150 000 km / 7 ans» разберётся так же, как «7 ans / 150 000 km».
  const yearsMatch = key.match(/(\d+)\s*(ans?|years?|yrs?|jahre?|god|goda|let|лет|года?)\b/);

  // Пробел, узкий пробел и точка внутри числа — разделители разрядов
  // («150 000», «150.000»), а не часть значения.
  const kmMatch = key.match(/([\d][\d\s\u00a0\u202f.,]*)\s*km\b/);
  const km = kmMatch ? Number(kmMatch[1].replace(/[\s\u00a0\u202f.,]/g, '')) : null;

  return {
    raw,
    years: yearsMatch ? Number(yearsMatch[1]) : null,
    km: Number.isFinite(km) && km > 0 ? km : null,
  };
}

// The largest bare number read as years. A bare "12", "24" or "36" is most likely
// months; it passes through unchanged, so the agent is never handed a 24-year warranty.
const MAX_BARE_YEARS = 10;

/**
 * The car's warranty term as the agent should read it: always with its unit (#1487).
 *
 * Andys stock keeps the term as free text. Cars added since 2026-07 carry a bare
 * number ("3"), older listings "3 years". b2b-api's own contract for the field is
 * "{num} years", and for Andys a bare number is a number of years (confirmed
 * 2026-10-16). So a bare number from 1 to MAX_BARE_YEARS becomes "N years". Any
 * other text — a unit already present, "7 ans / 150 000 km", a bare number out of
 * that range — passes through unchanged. An empty value stays null, so "no term
 * recorded" never looks like a term.
 *
 * @param {unknown} value — the car's `warrantyTerm`.
 * @returns {string|null}
 */
export function warrantyTermWithUnit(value) {
  const raw = typeof value === 'number' && Number.isInteger(value)
    ? String(value)
    : typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    const years = Number(raw);
    if (years < 1 || years > MAX_BARE_YEARS) return raw;
    return years === 1 ? '1 year' : `${years} years`;
  }
  return raw;
}

export default parseWarrantyTerm;
